


import { useEffect, useState } from "react";
import Select from "react-select";
import { Check, AlertCircle } from "lucide-react";
import toast from "react-hot-toast";
import { axiosInstance } from "../../../utility/apiInstance";
import { MilestoneState, TabFSUSProps } from "./types/MilestoneType";
import { formatDateTime, mapToMilestoneState } from "./utils";
import { useRole } from "../../MangeContextProvide";

// ── Styles ────────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  root: { padding: "14px 0 0" },

  // section header
  sectionHeader: {
    display: "flex", alignItems: "center",
    justifyContent: "space-between", marginBottom: "12px",
  },
  sectionTitle: { display: "flex", alignItems: "center", gap: "7px" },
  sectionAccent: { width: "3px", height: "16px", background: "#F8B71F", borderRadius: "99px" },
  sectionLabel: {
    fontSize: "12px", fontWeight: 600, color: "#374151",
    textTransform: "uppercase" as const, letterSpacing: "0.06em",
  },
  rowCount: { fontSize: "11px", color: "#9ca3af", fontWeight: 500 },

  // column headers
  colHeaders: {
    display: "grid",
    gridTemplateColumns: "160px 170px 100px 90px 90px 130px 130px 64px",
    padding: "0 12px",
    marginBottom: "6px",
    minWidth: "700px",
  },
  colHeader: {
    fontSize: "10.5px", fontWeight: 600, color: "#9ca3af",
    textTransform: "uppercase" as const, letterSpacing: "0.06em", padding: "0 5px",
  },

  // row
  rowWrap: { position: "relative" as const, marginBottom: "6px" },
  rowCard: {
    display: "grid",
    gridTemplateColumns: "160px 170px 100px 90px 90px 130px 130px 64px",
    background: "#fff",
    border: "1px solid #efefef",
    borderRadius: "10px",
    padding: "9px 12px",
    alignItems: "center",
    transition: "box-shadow 0.15s, border-color 0.15s",
    minWidth: "700px",
  },
  rowCardCompleted: { background: "#f9fafb" },
  rowCardPending: { borderLeft: "3px solid #F8B71F" },
  rowCardPart: { borderLeft: "3px solid #a78bfa" },
  rowCardHovered: { borderColor: "#e0e0e0", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" },
  cell: { padding: "0 5px" },

  // milestone name
  milestoneName: { fontSize: "12.5px", fontWeight: 600, color: "#1a1a1a" },
  milestoneNameCompleted: { fontSize: "12.5px", fontWeight: 500, color: "#6b7280" },
  partBadge: {
    display: "inline-block", fontSize: "10px", fontWeight: 600,
    background: "#ede9fe", color: "#6d28d9",
    padding: "1px 6px", borderRadius: "4px",
    marginLeft: "5px", verticalAlign: "middle",
  },

  // flight chip (selected/completed)
  flightChip: {
    display: "inline-flex", alignItems: "center", gap: "5px",
    background: "#f3f4f6", border: "1px solid #e5e7eb",
    borderRadius: "6px", padding: "4px 8px",
    fontSize: "11.5px", fontWeight: 600, color: "#374151",
  },
  flightChipRoute: { fontSize: "10.5px", color: "#9ca3af", fontWeight: 400 },

  // location
  locVal: { fontSize: "12px", color: "#374151" },
  locNa: { fontSize: "12px", color: "#9ca3af" },

  // number inputs
  numInput: {
    border: "1px solid #e2e2e2", borderRadius: "6px",
    padding: "5px 8px", fontSize: "12px", fontFamily: "inherit",
    color: "#374151", background: "#fff", outline: "none", width: "76px",
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  numInputError: { borderColor: "#ef4444" },
  numInputDisabled: { background: "#f9f9f9", color: "#9ca3af", cursor: "not-allowed" },
  numVal: { fontSize: "12.5px", color: "#374151", fontWeight: 500 },

  // error
  errText: {
    color: "#ef4444", fontSize: "10.5px",
    marginTop: "3px", display: "flex", alignItems: "center", gap: "3px",
  },

  // dates
  dateVal: { fontSize: "12px", color: "#374151" },
  dateNa: { fontSize: "12px", color: "#9ca3af" },
  dateInput: {
    border: "1px solid #e2e2e2", borderRadius: "6px",
    padding: "5px 7px", fontSize: "11px", fontFamily: "inherit",
    color: "#374151", background: "#fff", outline: "none",
    width: "122px", cursor: "pointer",
    transition: "border-color 0.15s, box-shadow 0.15s",
  },

  // badges
  badgeDone: {
    display: "inline-flex", alignItems: "center", gap: "4px",
    background: "#dcfce7", color: "#166534",
    fontSize: "11px", fontWeight: 600,
    padding: "3px 8px", borderRadius: "5px",
  },
  badgePending: {
    display: "inline-flex", alignItems: "center", gap: "4px",
    background: "#fef9c3", color: "#854d0e",
    fontSize: "11px", fontWeight: 600,
    padding: "3px 8px", borderRadius: "5px",
  },
};

// ── react-select styles ───────────────────────────────────────────────────────

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    fontSize: "11.5px",
    minHeight: "30px",
    minWidth: "150px",
    borderColor: state.isFocused ? "#F8B71F" : "#e2e2e2",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(248,183,31,0.18)" : "none",
    "&:hover": { borderColor: "#F8B71F" },
    borderRadius: "6px",
    background: "#fff",
  }),
  option: (base: any, state: any) => ({
    ...base, fontSize: "11.5px",
    background: state.isSelected ? "#F8B71F" : state.isFocused ? "#fef9c3" : "#fff",
    color: state.isSelected ? "#1a1a1a" : "#374151",
  }),
  placeholder: (base: any) => ({ ...base, fontSize: "11.5px", color: "#9ca3af" }),
  menu: (base: any) => ({ ...base, borderRadius: "8px", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 20 }),
  valueContainer: (base: any) => ({ ...base, padding: "2px 6px" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base: any) => ({ ...base, padding: "3px" }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

// ── Sub-components ────────────────────────────────────────────────────────────

function ErrorHint({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div style={s.errText}>
      <AlertCircle size={10} />
      {message}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

function TabFsus({ awbNumber, isOnline }: TabFSUSProps) {
  const { trackingData, setTrackingData } = useRole() as any;

  const [milestones, setMilestones] = useState<MilestoneState[]>([]);
  const [milestoneError, setMilestoneError] = useState<{ pieces: string; weight: string }[]>([]);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  // ── Fetch ───────────────────────────────────────────────────────────────────

  const fetchMilestones = async (awb: string) => {
    if (!awb) return;
    try {
      const { data } = await axiosInstance.get(`/api/v1/offline-milestone/${awb}`);
      setMilestones(mapToMilestoneState(data.data, awb, isOnline));
    } catch (err: any) {
      setMilestones([]);
      toast.error(err.response?.data?.message || err.response?.data?.error || "Something went wrong!");
    }
  };

  const fetchTrackingData = async () => {
    try {
      const { data } = await axiosInstance.post("/api/v1/tracking/data", { type: "air", trackingNumber: awbNumber });
      setTrackingData(data.data);
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.response?.data?.error || "Something went wrong!");
    }
  };

  useEffect(() => {
    if (!awbNumber) return;
    fetchMilestones(awbNumber);
  }, [awbNumber]);

  useEffect(() => {
    if (milestones.length > 0) {
      setMilestoneError(milestones.map(() => ({ pieces: "", weight: "" })));
    }
  }, [milestones]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleFlightSelect = (index: number, value: string, selectedFlight: any, event: MilestoneState) => {
    setMilestones(prev => prev.map((m, i) =>
      i === index ? {
        ...m,
        flightNumber: value,
        origin: selectedFlight.origin,
        destination: selectedFlight.destination,
        estimatedDate: event.mileStone.includes("Departure") ? selectedFlight.etd : selectedFlight.eta,
        station: event.station,
      } : m
    ));
  };

  const handleChange = async (index: number, field: keyof MilestoneState, value: any) => {
    // Numeric validation
    if (field === "pieces" || field === "weight") {
      if (!/^\d+(\.\d*)?$/.test(value) && value !== "") return;

      if (value === "") {
        setMilestoneError(prev => {
          const updated = [...prev];
          updated[index][field] = `${field} must be greater than 0`;
          return updated;
        });
        return;
      }

      const num = Number(value);
      const current = milestones[index];
      const isGateIn = current.mileStone.toLowerCase() === "gate in";

      if (field === "pieces" && !isGateIn && num > (current.maxPkts || 0)) {
        setMilestoneError(prev => {
          const updated = [...prev];
          updated[index].pieces = `Pieces cannot be greater than ${current.maxPkts || 0}`;
          return updated;
        });
        return;
      }

      if (field === "weight" && !isGateIn && num > (current.maxWeights || 0)) {
        setMilestoneError(prev => {
          const updated = [...prev];
          updated[index].weight = `Weight cannot be greater than ${current.maxWeights || 0}`;
          return updated;
        });
        return;
      }

      setMilestoneError(prev => {
        const updated = [...prev];
        updated[index][field] = "";
        return updated;
      });
    }

    const updatedMilestones = milestones.map((m, i) => i === index ? { ...m, [field]: value } : m);
    setMilestones(updatedMilestones);

    // Auto-save on date change
    if (field === "date") {
      try {
        // if (index > 0) {
        //   const preViousMilestone = milestones[index-1];
        //   if (preViousMilestone&&preViousMilestone.status==0) {
        //     const blockedMilestone = preViousMilestone.mileStone;
        //     toast.error(`Complete "${blockedMilestone}" first before updating this milestone.`);
        //     // Reset the date value back
        //     setMilestones(prev => prev.map((m, i) => i === index ? { ...m, date: "" } : m));
        //     return;
        //   }
        // }

        const firstIncomplete = milestones.findIndex(m => m.status !== 1&&m?.isPart==false);
        if (firstIncomplete !== -1 && index > firstIncomplete) {
          const blockedMilestone = milestones[firstIncomplete].mileStone;
          toast.error(`Complete "${blockedMilestone}" first before updating this milestone.`);
          // Reset the date value back
          setMilestones(prev => prev.map((m, i) => i === index ? { ...m, date: "" } : m));
          return;
        }

        const current = updatedMilestones[index];
        const previous = updatedMilestones[index - 1];
        if(current.mileStone.toLowerCase() === "delivered" && previous?.status !== 1) {
          toast.error(`Complete previous milestone "${previous?.mileStone}" first before updating "Delivered".`);
          // Reset the date value back
          setMilestones(prev => prev.map((m, i) => i === index ? { ...m, date: "" } : m));
          return;
        }



        const target = updatedMilestones[index];
        if ((target?.flights || []).length === 0 && index > 0 && index < updatedMilestones.length - 1) {
          toast.error("Please select a Flight");
          return;
        }
        await axiosInstance.post("/api/v1/offline-milestone/create", [target]);
        fetchMilestones(awbNumber);
        toast.success("Milestone updated");
      } catch {
        toast.error("Failed to update.");
      } finally {
        fetchTrackingData();
      }
    }
  };

  // ── Derived ─────────────────────────────────────────────────────────────────

  const completedCount = milestones.filter(m => m.status === 1).length;

  const COLS = ["Milestone", "Flight", "Station", "Pkts", "Weight", "ETA / ETD", "Actual Date", "Status"];

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={s.root}>

      {/* Section header */}
      <div style={s.sectionHeader}>
        <div style={s.sectionTitle}>
          <div style={s.sectionAccent} />
          <span style={s.sectionLabel}>Flight Status Updates</span>
        </div>
        {milestones.length > 0 && (
          <span style={s.rowCount}>
            {milestones.length} milestone{milestones.length !== 1 ? "s" : ""}
            {completedCount > 0 && ` · ${completedCount} completed`}
          </span>
        )}
      </div>

      <div style={{ overflowX: "auto" }}>

        {/* Column headers */}
        <div style={s.colHeaders}>
          {COLS.map(h => <span key={h} style={s.colHeader}>{h}</span>)}
        </div>

        {/* Rows */}
        {milestones.map((event, index) => {
          const isCompleted = event.status === 1;
          const isPart = event.isPart === true;
          const isHovered = hoveredRow === index;
          const isInputDisabled = isOnline || ((event?.flights || []).length == 0 && index != 0 && index != (milestones.length - 1));

          const rowStyle: React.CSSProperties = {
            ...s.rowCard,
            ...(isCompleted ? s.rowCardCompleted : {}),
            ...(!isCompleted && isPart ? s.rowCardPart : {}),
            ...(!isCompleted && !isPart ? s.rowCardPending : {}),
            ...(isHovered ? s.rowCardHovered : {}),
          };

          return (
            <div
              key={event.mileStoneSequence}
              style={s.rowWrap}
              onMouseEnter={() => setHoveredRow(index)}
              onMouseLeave={() => setHoveredRow(null)}
            >
              <div style={rowStyle}>

                {/* Milestone name */}
                <div style={s.cell}>
                  <span style={isCompleted ? s.milestoneNameCompleted : s.milestoneName}>
                    {event.mileStone}
                  </span>
                  {isPart && <span style={s.partBadge}>Part</span>}
                </div>

                {/* Flight */}
                <div style={s.cell}>
                  {isCompleted || isOnline ? (
                    event.flightNumber ? (
                      <div style={s.flightChip}>
                        {event.flightNumber}
                        {event.origin && event.destination && (
                          <span style={s.flightChipRoute}>
                            {event.origin} → {event.destination}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={s.locNa}>N/A</span>
                    )
                  ) : event.flights && event.flights.length > 0 ? (
                    <Select
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      options={event.flights.map((f: any) => ({
                        label: f.flightNumber,
                        value: f.flightNumber,
                        flight: f,
                      }))}
                      value={
                        event.flightNumber
                          ? {
                            label: event.flightNumber,
                            value: event.flightNumber,
                          }
                          : null
                      }
                      placeholder="Select Flight"
                      onChange={(sel: any) =>
                        handleFlightSelect(index, sel.value, sel.flight, event)
                      }
                    />
                  ) : (
                    <span style={s.locNa}>No flights</span>
                  )}
                </div>

                {/* Station */}
                <div style={s.cell}>
                  {event.station
                    ? <span style={s.locVal}>{event.station}</span>
                    : <span style={s.locNa}>—</span>
                  }
                </div>

                {/* Pieces */}
                <div style={s.cell}>
                  {isCompleted ? (
                    <span style={s.numVal}>{event.pieces} Pkts</span>
                  ) : (
                    <>
                      <input
                        type="text"
                        style={{
                          ...s.numInput,
                          ...(isOnline ? s.numInputDisabled : {}),
                          ...(milestoneError[index]?.pieces ? s.numInputError : {}),
                        }}
                        placeholder="Pkts"
                        value={event.pieces ?? ""}
                        disabled={isInputDisabled}
                        onChange={e => handleChange(index, "pieces", +e.target.value)}
                      />
                      <ErrorHint message={milestoneError[index]?.pieces} />
                    </>
                  )}
                </div>

                {/* Weight */}
                <div style={s.cell}>
                  {isCompleted ? (
                    <span style={s.numVal}>{event.weight} Kgs</span>
                  ) : (
                    <>
                      <input
                        type="text"
                        style={{
                          ...s.numInput,
                          ...(isOnline ? s.numInputDisabled : {}),
                          ...(milestoneError[index]?.weight ? s.numInputError : {}),
                        }}
                        placeholder="Kgs"
                        value={event.weight ?? ""}
                        disabled={isInputDisabled}
                        onChange={e => handleChange(index, "weight", +e.target.value)}
                      />
                      <ErrorHint message={milestoneError[index]?.weight} />
                    </>
                  )}
                </div>

                {/* ETA / ETD */}
                <div style={s.cell}>
                  {event.estimatedDate
                    ? <span style={s.dateVal}>
                      {isOnline ? event.estimatedDate : formatDateTime(event.estimatedDate)}
                    </span>
                    : <span style={s.dateNa}>N/A</span>
                  }
                </div>

                {/* Actual date */}
                <div style={s.cell}>
                  {isCompleted ? (
                    <span style={s.dateVal}>{event.date}</span>
                  ) : (isOnline ? (
                    <span style={event.estimatedDate ? s.dateVal : s.dateNa}>
                      {event.date || "N/A"}
                    </span>
                  ) : (
                    <input
                      type="date"
                      style={s.dateInput}
                      value={event.date || ""}
                      onMouseDown={(e: any) => { e.preventDefault(); e.target.showPicker(); }}
                      onChange={e => handleChange(index, "date", e.target.value)}
                      disabled={isInputDisabled}
                    />
                  ))
                  }
                </div>

                {/* Status */}
                <div style={s.cell}>
                  {isCompleted ? (
                    <span style={s.badgeDone}>
                      <Check size={10} />
                      Done
                    </span>
                  ) : (
                    <span style={s.badgePending}>Pending</span>
                  )}
                </div>

              </div>
            </div>
          );
        })}

      </div>
    </div>
  );
}

export default TabFsus;
